import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { findAllDeletion, findOneDeletion } from "./api";
import { attachToRemarks, employeeProfile, formatApprover, fromSapDate, getSessionUser, remarksFromToRemarks } from "./helpers";
import { CreateBar, EmptyState, Line, PageShell, RequestCard, StatusBadge } from "./ui";

type Row = {
  referenceNumber: string;
  dependentName: string;
  remarks: string;
  creationDate: string;
  status: string;
  approver: string;
};

const mapItem = (item: any): Row => {
  const firstName = item?.RelativeFirstName ?? item?.relativeFirstName ?? "";
  const lastName = item?.RelativeLastName ?? item?.relativeLastName ?? "";
  const dependentName = String(item?.DependentName ?? item?.dependentName ?? `${firstName} ${lastName}`).trim();
  return {
    referenceNumber: String(item?.ReferenceNumber ?? item?.referenceNumber ?? ""),
    dependentName,
    remarks: remarksFromToRemarks(item),
    creationDate: fromSapDate(item?.RequestCreationDate ?? item?.requestCreationDate ?? item?.CreationDate),
    status: String(item?.RequestStatus ?? item?.Status ?? item?.status ?? ""),
    approver: formatApprover(
      item?.ApproverName ?? item?.approverName ?? item?.LatestApproverName,
      item?.ApproverUserID ?? item?.approverUserID
    ),
  };
};

const DeletionParentsList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = useCallback(async () => {
    const empId = employeeProfile(getSessionUser()).employeeNo;
    if (!empId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await findAllDeletion(empId);
      if (res.ok) {
        const withRemarks = await attachToRemarks(res.list, findOneDeletion);
        setRows(withRemarks.map(mapItem));
      }
      else toast.error(res.message || "Something went wrong");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return (
    <PageShell title="Inclusion and Deletion" subtitle="Deletion of dependent parents including widow mother">
      <CreateBar label="Create New Request" onClick={() => navigate(all_routes.idCard.deletionOfDependent + "/create")} />
      {loading && <div className="text-center py-4">Loading...</div>}
      {!loading &&
        rows.map((item) => (
          <RequestCard
            key={item.referenceNumber}
            reference={item.referenceNumber}
            lines={
              <>
                <Line label="Dependent Name" value={item.dependentName || "-"} />
                <Line label="Request Creation Date" value={item.creationDate || "-"} />
                <Line label="Last/Latest Approver" value={item.approver} />
                <Line label="Request Status" value={<StatusBadge status={item.status} />} />
                <Line wide label="Remark" value={item.remarks || "-"} />
              </>
            }
          />
        ))}
      {!loading && rows.length === 0 && <EmptyState />}
    </PageShell>
  );
};

export default DeletionParentsList;
